import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { INDIA_LOCATION_PAGES } from "@/lib/locations";
import { SITE_ORIGIN, sectionImageMeta, SECTION_OG } from "@/lib/ogSections";
import { hreflangLinks } from "@/lib/hreflang";
import { pageSchemaScript } from "@/seo/schema";


const LOCATIONS_TITLE = "Security Consultant Locations in India | Arif Khan";
const LOCATIONS_DESC =
  "Find security infrastructure, automation and AV consulting for every Indian state and Delhi NCR city, including Delhi, Gurugram, Noida, Faridabad and Ghaziabad.";

export const Route = createFileRoute("/locations/")({
  component: LocationsHub,
  head: () => ({
    meta: [
      { title: LOCATIONS_TITLE },
      { name: "description", content: LOCATIONS_DESC },
      { property: "og:title", content: LOCATIONS_TITLE },
      { property: "og:description", content: "Location-aware security, automation and AV planning across India and Delhi NCR." },
      { property: "og:url", content: `${SITE_ORIGIN}/locations` },
      { property: "og:type", content: "website" },
      ...sectionImageMeta(SECTION_OG.industries, "Security consultant locations across India"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: LOCATIONS_TITLE },
      { name: "twitter:description", content: "Location-aware security, automation and AV planning across India and Delhi NCR." },
    ],
    links: [{ rel: "canonical", href: `${SITE_ORIGIN}/locations` }, ...hreflangLinks("/locations")],
    scripts: [
      pageSchemaScript(
        {
          path: "/locations",
          type: "CollectionPage",
          name: LOCATIONS_TITLE,
          description: LOCATIONS_DESC,
          breadcrumb: [{ name: "Locations", url: "/locations" }],
        },
        [
          {
            "@type": "ItemList",
            "@id": `${SITE_ORIGIN}/locations#list`,
            name: "Arif Khan locations across India",
            numberOfItems: INDIA_LOCATION_PAGES.length,
            itemListElement: INDIA_LOCATION_PAGES.map((location, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: location.name,
              url: `${SITE_ORIGIN}/locations/${location.slug}`,
            })),
          },
        ],
      ),
    ],

  }),
});

function LocationsHub() {
  const cities = INDIA_LOCATION_PAGES.filter((location) => location.kind === "city");
  const states = INDIA_LOCATION_PAGES.filter((location) => location.kind === "state");
  return (
    <PageShell>
      <PageHero eyebrow="India · Locations" title="Security and building technology planning, wherever the project is." description="Detailed location pages for India and Delhi NCR — helping owners, architects, developers and operators plan security infrastructure, automation, AV and cinema around the real site." />
      <section className="py-20">
        <div className="container-page">
          <div className="flex items-center gap-2 text-ink-soft"><MapPin className="h-4 w-4 text-primary" /><span className="eyebrow">Delhi NCR cities</span></div>
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {cities.map((location, index) => <LocationCard key={location.slug} location={location} index={index} />)}
          </div>
        </div>
      </section>
      <section className="py-20 bg-surface border-y border-border">
        <div className="container-page">
          <span className="eyebrow">States and union territories</span>
          <h2 className="mt-5 max-w-2xl text-3xl md:text-4xl font-semibold tracking-tight">India-wide coverage with a site-aware point of view.</h2>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {states.map((location) => <Link key={location.slug} to="/locations/$slug" params={{ slug: location.slug }} className="group rounded-xl border border-border bg-background p-5 hover:border-border-strong transition-colors"><span className="font-medium">{location.name}</span><span className="mt-2 block text-xs text-ink-soft">Security, automation and AV planning</span><ArrowRight className="mt-5 h-4 w-4 text-primary transition-transform group-hover:translate-x-1" /></Link>)}
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function LocationCard({ location, index }: { location: typeof INDIA_LOCATION_PAGES[number]; index: number }) {
  return <Reveal delay={index * 0.05}><Link to="/locations/$slug" params={{ slug: location.slug }} className="group block h-full rounded-2xl border border-border bg-surface p-7 hover:border-border-strong transition-colors"><span className="font-mono text-xs text-primary">0{index + 1}</span><h2 className="mt-5 text-xl font-semibold tracking-tight">{location.name}</h2><p className="mt-3 text-sm leading-relaxed text-ink-soft">{location.summary}</p><span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-primary">Explore location <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span></Link></Reveal>;
}
